/**
 * AddEditSessionV2 — full V2 port of the Session edit form, built on
 * the new `src/components/redesign/edit` primitives (FormSection,
 * FieldBlock, TextInput, Textarea, ToggleSwitch, FieldRow).
 *
 * Architecture mirrors v1 1:1 — same state shape, same fetch graph,
 * same submit handler, same media-upload helpers — so the save flow
 * is byte-identical and we don't risk regressing the API contract.
 *
 * Sections (each is a FormSection card with a numbered chip):
 *   01 Basic info       — name, parent, author
 *   02 Description      — description + 4 inline toggles
 *   03 Tags             — TagsSection (v1 sub-component, kept as-is)
 *   04 Settings         — dark mode / no avatar / recommended /
 *                         chapter / skip captions + membership chips
 *   05 Sharing & Media  — SessionMediaSection (v1 sub-component, kept
 *                         as-is — file uploads + deep-link images)
 *   06 Background music — BackgroundMusicSelector + content / rating
 *      & content markers   timestamps
 *   07 Tapping points   — TappingPointsTimestamp (v1 sub-component)
 *   08 Delete (edit-only) — danger button at the bottom
 *
 * Composite sub-pickers (AuthorSelector, ParentSelector,
 * BackgroundMusicSelector, TagsSection, SessionMediaSection,
 * TappingPointsTimestamp) keep their MUI internals — restyling them
 * is its own scope. They sit inside V2 FieldBlock containers so the
 * surrounding spacing/labels feel native to the redesign.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';

import useSessionFormMedia from '../useSessionFormMedia';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import { selectTappingPoints, selectBackgroundMusics } from '../selectors';

import { fetchAllBGM } from '../../../actions/backgroundMusicActions';
import { fetchAllChallenges } from '../../../actions/challengeActions';
import { fetchAuthors } from '../../../actions/authorsActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import { clearMedia, uploadAudio, uploadImage } from '../../../actions/mediaActions';
import { fetchAllSeries } from '../../../actions/seriesActions';
import { addEditSession, deleteSession, fetchOneSession, clearSessions } from '../../../actions/sessionActions';
import { fetchRecommendedList, fetchPopularList } from '../../../actions/sessionListsActions';
import { fetchAllSubCategories } from '../../../actions/subcategoryActions';
import { fetchTappingPoints } from '../../../actions/tappingPointsActions';
import { addObjectTag, removeObjectTag } from '../../../actions/tagActions';
import { formatTimeToMMSS } from '../../../utils/timeFormat';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button, StatusPill, Tooltip } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  TagsInput,
  LinkField,
  AuthorSelectButton,
  ParentSelectButton,
  UploadTile,
  WaveformPlayer,
  BgmCard,
} from '../../redesign/edit';
import PublishingCard from './PublishingCard';
import SettingsCard from './SettingsCard';
import PreviewCard from './PreviewCard';
import TappingPointsEditor from './TappingPointsEditor';
import ContentMarkers from './ContentMarkers';

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function AddEditSessionV2({ isEdit, selectedId, onLoaded }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─── Selectors (1:1 with v1) ──────────────────────────────────── */
  const tapping_points_raw = useSelector(selectTappingPoints);
  const tapping_points = useMemo(
    () =>
      Array.isArray(tapping_points_raw)
        ? tapping_points_raw
        : tapping_points_raw
          ? Object.values(tapping_points_raw)
          : [],
    [tapping_points_raw],
  );
  const background_musics = useSelector(selectBackgroundMusics);
  const recommendedListItems = useSelector((s) => s.recommended_list?.items);
  const popularListItems = useSelector((s) => s.popular_list?.items);

  const isOnRecommendedList = useMemo(() => {
    if (!Array.isArray(recommendedListItems) || !selectedId) return false;
    const id = Number(selectedId);
    return recommendedListItems.some((s) => Number(s.session_id) === id);
  }, [recommendedListItems, selectedId]);

  const isOnPopularList = useMemo(() => {
    if (!Array.isArray(popularListItems) || !selectedId) return false;
    const id = Number(selectedId);
    return popularListItems.some((s) => Number(s.session_id) === id);
  }, [popularListItems, selectedId]);

  /* ─── State (1:1 with v1) ──────────────────────────────────────── */
  const [formData, setFormData] = useState({
    session_name: '',
    session_allow_rating: 0,
    session_text_page_url: '',
    session_text_page_ios_url: '',
    session_description: '',
    session_main_url: '',
    session_main_url_preview: '',
    session_main_url_uploading: false,
    session_music_url: '',
    session_music_url_preview: '',
    session_music_url_uploading: false,
    session_image: '',
    session_image_preview: '',
    session_image_uploading: false,
    session_text_image_url: '',
    session_text_image_url_preview: '',
    session_text_image_url_uploading: false,
    session_grayscale_image: '',
    session_grayscale_image_preview: '',
    session_grayscale_image_uploading: false,
    session_is_active: 0,
    session_is_chapter: 0,
    session_skip_captions: 0,
    session_is_free: 1,
    session_media_type: 1,
    session_rating_start: 0,
    session_rating_end: 0,
    no_avatar: 0,
    session_is_recommended: 0,
    session_author_id: '',
    session_bg_timestamps: [{ tapping_point_id: '', timestamp: 0 }],
    associatedParent: '0',
    associatedSeries: '0',
    session_content_start_timestamp: 0,
    session_label: '',
    session_tags: [],
    tags: [],
    session_length: '',
    session_length_in_sec: 0,
    session_is_new: 0,
    session_is_dark_mode: 0,
    is_bgm_locked: 0,
    session_bgm_id: '0',
    session_is_duplicate: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const timestampComponentRef = useRef(null);

  // Latest-value ref for the page chrome's onLoaded callback —
  // lets the load effect stay keyed on `selectedId` alone without
  // re-firing when the parent re-renders with a fresh function.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const renderBGMOptions = useCallback(() => {
    if (!background_musics) return [];
    const options = background_musics.map((obj) => ({
      label: obj.bgm_title,
      value: String(obj.bgm_id),
      prop: 'session_bgm_id',
      session_music_url: obj.bgm_url,
    }));
    const none = {
      label: 'none',
      value: '0',
      prop: 'session_bgm_id',
      session_music_url: '',
    };
    return [none, ...options];
  }, [background_musics]);

  /* ─── Load data (1:1 with v1) ──────────────────────────────────── */
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        dispatch(clearMedia());
        dispatch(clearSessions());

        await Promise.all([
          dispatch(fetchAllCategories(0)),
          dispatch(fetchAllSubCategories(null, 0)),
          dispatch(fetchAuthors()),
          dispatch(fetchAllChallenges()),
          dispatch(fetchAllSeries()),
          dispatch(fetchTappingPoints()),
          dispatch(fetchAllBGM()),
        ]);

        if (isEdit) {
          dispatch(fetchRecommendedList()).catch(() => {});
          dispatch(fetchPopularList()).catch(() => {});

          setIsLoadingSession(true);
          try {
            const response = await dispatch(fetchOneSession(selectedId));
            if (response?.data?.objects?.[0]) {
              const sessionData = response.data.objects[0];
              let associatedParent = '0';
              if (sessionData.category_id) {
                associatedParent = `${sessionData.category_id}CAT`;
              } else if (sessionData.subcategory_id) {
                associatedParent = `${sessionData.subcategory_id}SUB`;
              } else if (sessionData.challenge_id) {
                associatedParent = `${sessionData.challenge_id}CHA`;
              } else if (sessionData.series_id) {
                associatedParent = `${sessionData.series_id}SER`;
              }
              setFormData({
                ...sessionData,
                session_bgm_id:
                  sessionData.session_bgm_id != null
                    ? String(sessionData.session_bgm_id)
                    : '0',
                associatedParent,
              });
              // Hand the page chrome just enough to render the
              // sticky header (title + updated_at) so it doesn't
              // have to fire its own parallel GET /sessions/{id}.
              if (onLoadedRef.current) {
                onLoadedRef.current({
                  session_name: sessionData.session_name,
                  session_updated_at:
                    sessionData.session_updated_at
                    ?? sessionData.updated_at
                    ?? null,
                });
              }
            } else {
              setError('Failed to load session data. Please try again.');
            }
          } catch (err) {
            setError('Failed to load session data. Please try again.');
          } finally {
            setIsLoadingSession(false);
          }
        }

        setIsLoading(false);
      } catch (err) {
        setError('Failed to load required data. Please try again.');
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, isEdit, selectedId]);

  // Side effects for media previews + redux upload completion
  useSessionFormMedia(formData, setFormData);

  /* ─── Loading / error states ───────────────────────────────────── */
  if (isLoading || isLoadingSession) {
    return <LoadingBackdrop open />;
  }
  if (error) {
    return (
      <FormSection title="Couldn't load session">
        <p className="text-coral" style={{ fontSize: 14, marginBottom: 12 }}>
          {error}
        </p>
        <Link to="/sessions" className="no-underline">
          <Button variant="default">Return to Sessions</Button>
        </Link>
      </FormSection>
    );
  }

  /* ─── Handlers (1:1 with v1) ───────────────────────────────────── */

  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleToggle = (name) => (e) =>
    setField(name, e.target.checked ? 1 : 0);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.session_image_preview) {
      URL.revokeObjectURL(formData.session_image_preview);
    }
    setFormData((prev) => ({
      ...prev,
      session_image_uploading: true,
      session_image_preview: URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file));
  };

  const handleMainMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.session_main_url_preview) {
      URL.revokeObjectURL(formData.session_main_url_preview);
    }
    const previewUrl = URL.createObjectURL(file);
    try {
      setFormData((prev) => ({
        ...prev,
        session_main_url_uploading: true,
        session_main_url_preview: previewUrl,
      }));
      const response = await dispatch(uploadAudio(file));
      if (!response?.data?.audio_file_url) {
        throw new Error('Upload failed - no audio URL received');
      }
    } catch (err) {
      console.error('Error uploading audio:', err);
      URL.revokeObjectURL(previewUrl);
      setFormData((prev) => ({
        ...prev,
        session_main_url_uploading: false,
        session_main_url_preview: '',
      }));
      Swal.fire({ icon: 'error', title: 'Upload Failed', text: 'Failed to upload audio file. Please try again.' });
    }
  };

  const handleAudioLoadedData = (e) => {
    const duration = e.target.duration;
    if (duration && !isNaN(duration)) {
      setFormData((prev) => ({
        ...prev,
        session_length_in_sec: Math.floor(duration),
        session_length: formatTimeToMMSS(duration),
      }));
    }
  };

  const handleDeepLinkImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.session_text_image_url_preview) {
      URL.revokeObjectURL(formData.session_text_image_url_preview);
    }
    setFormData((prev) => ({
      ...prev,
      session_text_image_url_uploading: true,
      session_text_image_url_preview: URL.createObjectURL(file),
    }));
    dispatch(uploadImage(file));
  };

  const handleTimestampChange = (value, stateProp, index, field) => {
    setFormData((prev) => ({
      ...prev,
      session_bg_timestamps: prev.session_bg_timestamps.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addTimestamp = () => {
    const newIndex = formData.session_bg_timestamps.length;
    setFormData((prev) => ({
      ...prev,
      session_bg_timestamps: [
        ...prev.session_bg_timestamps,
        { tapping_point_id: '', timestamp: 0 },
      ],
    }));
    if (timestampComponentRef.current) {
      timestampComponentRef.current.focusOnNewItem(newIndex);
    }
  };

  const removeTimestamp = (index) => {
    setFormData((prev) => ({
      ...prev,
      session_bg_timestamps: prev.session_bg_timestamps.filter(
        (_, i) => i !== index,
      ),
    }));
  };

  const handleImportTimestamps = (importedTimestamps) => {
    setFormData((prev) => ({
      ...prev,
      session_bg_timestamps: [
        ...prev.session_bg_timestamps,
        ...importedTimestamps.map((ts) => ({
          tapping_point_id: ts.tapping_point_id,
          timestamp: ts.timestamp,
          image_url: ts.image_url,
        })),
      ],
    }));
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "This will delete the session. You can't undo this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });
    if (result.isConfirmed) {
      dispatch(deleteSession(selectedId, () => navigate('/sessions')));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const parentValue = formData.associatedParent;
      let category_id = 0;
      let subcategory_id = 0;
      let series_id = 0;
      let challenge_id = 0;
      if (parentValue !== '0') {
        const [id, type] = parentValue.split(/(?<=[0-9])(?=[A-Z])/);
        switch (type) {
          case 'CAT': category_id = parseInt(id); break;
          case 'SUB': subcategory_id = parseInt(id); break;
          case 'SER': series_id = parseInt(id); break;
          case 'CHA': challenge_id = parseInt(id); break;
          default: break;
        }
      }
      const sessionData = {
        ...formData,
        category_id,
        subcategory_id,
        series_id,
        challenge_id,
      };
      delete sessionData.associatedParent;
      delete sessionData.tags;
      delete sessionData.session_image_preview;
      delete sessionData.session_main_url_preview;
      delete sessionData.session_text_image_url_preview;
      delete sessionData.session_image_uploading;
      delete sessionData.session_main_url_uploading;
      delete sessionData.session_text_image_url_uploading;
      const response = await dispatch(
        addEditSession({
          timestamps_json: JSON.stringify(formData.session_bg_timestamps),
          session_json: JSON.stringify(sessionData),
          category_id,
          subcategory_id,
          series_id,
          challenge_id,
        }),
      );
      if (response?.payload) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: isEdit ? 'Session updated successfully!' : 'Session created successfully!',
          timer: 1000,
        }).then(() => {
          navigate('/sessions');
        });
      }
    } catch (err) {
      console.error('Error saving session:', err);
      const apiMessage = err?.response?.data?.message;
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: apiMessage || 'Failed to save session. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  /* ─── Render ───────────────────────────────────────────────────── */

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      {/* `.edit-canvas` grid — main column + right rail. The rail
       *  collapses under the form at narrow widths (≤1080px in the
       *  prototype). The form keeps its own `id="session-form"` so
       *  the page-level header Save button submits via `form="…"`.
       *  Tailwind `lg:` breakpoint (≥1024px) flips on the 2-col
       *  layout; below that the rail stacks under the form. */}
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
      <form
        id="session-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0 }}
      >
        {/* 01 — Basic info. The prototype stacks Title / Description /
         *  Author in their own field-blocks rather than a 3-col grid,
         *  with Author capped to ~480px (line 8205). Parent gets its
         *  own numbered section in the prototype, so it moves to 02
         *  here. */}
        <FormSection
          title="Basic info"
          num="01"
          right={
            formData.session_is_duplicate ? (
              <StatusPill tone="amber">Duplicated Session</StatusPill>
            ) : null
          }
        >
          <FieldBlock label="Title" required htmlFor="f-session-name">
            <TextInput
              id="f-session-name"
              name="session_name"
              value={formData.session_name}
              onChange={handleFieldChange}
              required
            />
          </FieldBlock>
          <FieldBlock
            label="Description"
            hint="Shown on the session card in-app"
            htmlFor="f-session-description"
          >
            <Textarea
              id="f-session-description"
              name="session_description"
              value={formData.session_description}
              onChange={handleFieldChange}
              rows={3}
            />
          </FieldBlock>
          <FieldBlock label="Author" required className="max-w-[480px]">
            <AuthorSelectButton
              value={formData.session_author_id}
              onChange={(value) => setField('session_author_id', value)}
            />
          </FieldBlock>
        </FormSection>

        {/* 02 — Parent (typeahead button + dropdown). The new
         *  ParentSelectButton handles the colored type-pill, the
         *  grouped + searchable dropdown, and the indented
         *  subcategories under categories — all matching the
         *  prototype's parent-select markup. */}
        <FormSection title="Parent" num="02">
          <FieldBlock
            label="Container"
            hint="A session belongs to exactly one — Category, Subcategory, Series, Challenge, or Audiobook. Type to search the full hierarchy."
            noMargin
          >
            <ParentSelectButton
              value={formData.associatedParent}
              onChange={(value) => setField('associatedParent', value)}
              allowedTypes={['category', 'subcategory', 'challenge', 'series', 'audiobook']}
              showQuickTaps={false}
            />
          </FieldBlock>
        </FormSection>


        {/* 03 — Tags (edit-only). The v1 TagsSection fires per-add /
         *  per-remove API calls (POST /object-tags / DELETE) instead
         *  of batching at form submit, so we mirror that here. The
         *  V2 TagsInput is pure-render — we adapt its string-array
         *  value to the v1 `{tag, newTag}` shape on the way in and
         *  out, and route mutations through addObjectTag /
         *  removeObjectTag. */}
        {/* 03 — Tags (edit-only). */}
        {isEdit && (
          <FormSection title="Tags" num="03">
            <FieldBlock
              label="Session tags"
              hint="Powers search, recommendations, and category cross-linking"
            >
              <TagsInput
                value={(formData.tags || []).map((t) => t.tag)}
                onChange={(nextStrings) => {
                  const prev = formData.tags || [];
                  const prevStrings = prev.map((t) => t.tag);
                  // Compute the diff so we know whether the user
                  // added or removed a tag this tick; per the v1
                  // semantics each mutation fires its own API call.
                  const added = nextStrings.filter((t) => !prevStrings.includes(t));
                  const removed = prevStrings.filter((t) => !nextStrings.includes(t));
                  added.forEach((tag) => {
                    dispatch(
                      addObjectTag({
                        object_id: formData.session_id,
                        object_type: 'Session',
                        tag,
                      }),
                    );
                  });
                  removed.forEach((tag) => {
                    dispatch(
                      removeObjectTag({
                        object_id: formData.session_id,
                        object_type: 'Session',
                        tag,
                      }),
                    );
                  });
                  setField(
                    'tags',
                    nextStrings.map((tag) => ({ tag, newTag: false })),
                  );
                }}
                placeholder="Type a tag and press enter…"
              />
            </FieldBlock>
          </FormSection>
        )}

        {/* All session boolean flags live in the right-rail
         *  SettingsCard (primary + advanced sub-section), so no
         *  in-form Settings section here. The recommended/popular
         *  membership chips still need a home — surface them inline
         *  next to the parent / tags so the form retains the same
         *  signal without adding a separate section just for them. */}
        {isEdit && (isOnRecommendedList || isOnPopularList) && (
          <FormSection
            title="List memberships"
            num={isEdit ? '04' : '03'}
          >
            <div
              className="flex items-center"
              style={{ gap: 8, flexWrap: 'wrap' }}
            >
              {isOnRecommendedList && (
                <Tooltip content="Managed via Engagement & Promotions → Default Recommended List">
                  <StatusPill tone="blue">Default Recommended</StatusPill>
                </Tooltip>
              )}
              {isOnPopularList && (
                <Tooltip content="Managed via Engagement & Promotions → Popular List">
                  <StatusPill tone="navy">Popular</StatusPill>
                </Tooltip>
              )}
            </div>
          </FormSection>
        )}

        {/* 05 — Sharing & Media. Mirrors the prototype's 2-col
         *  upload-tile grid (Session Image + Deep Link Image) and
         *  drops the universal link underneath. Audio moved to its
         *  own section below so this stays focused on the share
         *  surface (images + canonical URL). */}
        <FormSection title="Sharing & Media" num={isEdit ? '05' : '04'}>
          <FieldRow cols={2} minColWidth={240}>
            <UploadTile
              label="Session Image"
              required
              hint="In-app cover (1024×1024)"
              value={formData.session_image_preview || formData.session_image}
              uploading={!!formData.session_image_uploading}
              accept="image/*"
              onChange={handleImageUpload}
              onRemove={() => {
                if (formData.session_image_preview) {
                  URL.revokeObjectURL(formData.session_image_preview);
                }
                setFormData((prev) => ({
                  ...prev,
                  session_image: '',
                  session_image_preview: '',
                }));
              }}
            />
            <UploadTile
              label="Deep Link Image"
              hint="Social share card (1200×630)"
              value={formData.session_text_image_url_preview || formData.session_text_image_url}
              uploading={!!formData.session_text_image_url_uploading}
              accept="image/*"
              onChange={handleDeepLinkImageUpload}
              onRemove={() => {
                if (formData.session_text_image_url_preview) {
                  URL.revokeObjectURL(formData.session_text_image_url_preview);
                }
                setFormData((prev) => ({
                  ...prev,
                  session_text_image_url: '',
                  session_text_image_url_preview: '',
                }));
              }}
            />
          </FieldRow>
          {isEdit && formData.universal_link && (
            <div style={{ marginTop: 16 }}>
              <FieldBlock
                label="Universal Link"
                hint="Generated automatically · used for share & deep links"
                noMargin
              >
                <LinkField value={formData.universal_link} />
              </FieldBlock>
            </div>
          )}
        </FormSection>

        {/* 06 — Session audio. Wavesurfer-backed waveform with
         *  click-to-seek + color-coded markers for content start,
         *  the rating window, and every tapping point timestamp.
         *  Marker positions update reactively when the user edits
         *  the matching field elsewhere in the form. */}
        <FormSection
          title="Session audio"
          num={isEdit ? '06' : '05'}
          right={
            formData.session_length ? (
              <span
                className="font-mono text-text-muted"
                style={{ fontSize: 11.5 }}
              >
                {formData.session_length}
              </span>
            ) : null
          }
        >
          <WaveformPlayer
            url={formData.session_main_url_preview || formData.session_main_url}
            uploading={!!formData.session_main_url_uploading}
            onUpload={handleMainMediaUpload}
            onLoadedMetadata={handleAudioLoadedData}
            onRemove={() => {
              if (formData.session_main_url_preview) {
                URL.revokeObjectURL(formData.session_main_url_preview);
              }
              setFormData((prev) => ({
                ...prev,
                session_main_url: '',
                session_main_url_preview: '',
                session_length_in_sec: 0,
                session_length: '',
              }));
            }}
            contentStart={formData.session_content_start_timestamp}
            ratingStart={formData.session_rating_start}
            ratingEnd={formData.session_rating_end}
            showRating={!!formData.session_allow_rating}
            tappingPoints={formData.session_bg_timestamps}
            tappingPointsCatalog={tapping_points}
          />
        </FormSection>

        {/* 07 — Background music + Content markers */}
        <FormSection title="Background music & content markers" num={isEdit ? '07' : '06'}>
          <FieldRow cols={2} minColWidth={300}>
            <FieldBlock label="Background music">
              <BgmCard
                options={renderBGMOptions()}
                selectedValue={formData.session_bgm_id}
                isLocked={!!formData.is_bgm_locked}
                audioUrl={formData.session_music_url}
                isLoading={isLoading}
                onSelectionChange={(newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    session_bgm_id: newValue ? newValue.value : '0',
                    session_music_url: newValue ? newValue.session_music_url : '',
                  }));
                }}
                onLockChange={(checked) => setField('is_bgm_locked', checked ? 1 : 0)}
                onRemove={() => {
                  setFormData((prev) => ({
                    ...prev,
                    session_bgm_id: '0',
                    session_music_url: '',
                  }));
                }}
              />
            </FieldBlock>
            <FieldBlock label="Content markers" hint="Player jumps to these timestamps">
              <ContentMarkers
                contentStart={formData.session_content_start_timestamp}
                allowRating={!!formData.session_allow_rating}
                ratingStart={formData.session_rating_start}
                ratingEnd={formData.session_rating_end}
                maxSec={formData.session_length_in_sec}
                onChange={(name, value) => setField(name, value)}
              />
            </FieldBlock>
          </FieldRow>
        </FormSection>

        {/* 08 — Tapping point timings */}
        <FormSection
          title="Tapping point timings"
          num={isEdit ? '08' : '07'}
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              {formData.session_bg_timestamps.length} marker{formData.session_bg_timestamps.length === 1 ? '' : 's'}
            </span>
          }
        >
          <TappingPointsEditor
            ref={timestampComponentRef}
            items={formData.session_bg_timestamps}
            catalog={tapping_points}
            removeTimestamp={removeTimestamp}
            stateProp="session_bg_timestamps"
            handleTimestampChange={handleTimestampChange}
            onImportTimestamps={handleImportTimestamps}
            onAddTimestamp={addTimestamp}
            totalDurationSec={formData.session_length_in_sec}
          />
        </FormSection>

        {/* 09 — Danger zone (edit-only) */}
        {isEdit && (
          <FormSection title="Danger zone" num="09">
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete}>
                Delete session
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — cannot be undone.
              </span>
            </div>
          </FormSection>
        )}
      </form>

      {/* Right rail — Preview / Publishing / Settings. The prototype
       *  orders these as Preview (1) → Publishing (2) → Settings (3)
       *  in the visible flow; we render them in that order here.
       *  Each card binds back to the same `formData` source of truth,
       *  so changes flow through the existing `setField` handler. */}
      <aside
        className="edit-rail"
        style={{
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          minWidth: 0,
        }}
      >
        <PreviewCard formData={formData} />
        {isEdit && (
          <PublishingCard
            formData={formData}
            onToggleActive={(next) => setField('session_is_active', next ? 1 : 0)}
          />
        )}
        <SettingsCard
          formData={formData}
          onChange={(name, value) => setField(name, value)}
        />
      </aside>
      </div>
    </>
  );
}
